// Auth & MFA API
import { api } from './client';

// Helper types for API requests
export interface CreateUserRequest {
  username: string;
  email: string;
  password: string;
  role: 'superuser' | 'admin' | 'user' | 'client_admin' | 'client';
  full_name?: string;
  phone?: string;
  client_id?: number;
  permissions?: string;
}

export interface UpdateUserRequest {
  username?: string;
  email?: string;
  password?: string;
  role?: 'superuser' | 'admin' | 'user' | 'client_admin' | 'client';
  full_name?: string;
  phone?: string;
  is_active?: boolean;
  client_id?: number;
  permissions?: string;
}

// MFA interfaces
export interface MFASetupResponse {
  setup_token: string;
  secret: string;
  qr_code_data_url: string;
  manual_entry_key: string;
  expires_at: string;
  issuer: string;
  account_name: string;
}

export interface MFAVerifyResponse {
  success: boolean;
  recovery_codes: string[];
  message: string;
}

export interface MFAStatusResponse {
  totp_enabled: boolean;
  totp_enabled_at?: string;
  recovery_codes_available: number;
  recovery_codes_generated_at?: string;
  trusted_devices: TrustedDevice[];
  mfa_required: boolean;
  device_trust_enabled: boolean;
  device_trust_days: number;
}

export interface TrustedDevice {
  id: number;
  device_id: string;
  friendly_name: string;
  user_agent: string;
  ip_address: string;
  created_at: string;
  last_used_at?: string;
  expires_at: string;
}

export interface MFALoginResponse {
  mfa_required: boolean;
  mfa_token: string;
  message: string;
}

export interface SystemMFASettings {
  id: number;
  mfa_enabled: boolean;
  mfa_required: boolean;
  device_trust_enabled: boolean;
  device_trust_days: number;
  recovery_code_count: number;
  max_mfa_attempts: number;
  mfa_lockout_minutes: number;
  updated_at: string;
}

export interface ClientMFASettings {
  id: number;
  client_id: number;
  mfa_required: boolean;
  device_trust_enabled: boolean;
  device_trust_days: number;
  updated_at: string;
}

// Auth API
export const authAPI = {
  login: (email: string, password: string) => {
    console.log('🔐 Attempting login for email:', email);
    return api.post('/login', { email, password });
  },
  register: (username: string, email: string, password: string) => {
    console.log('📝 Attempting registration for user:', username);
    return api.post('/register', { username, email, password });
  },
  refreshToken: () => {
    console.log('🔄 Attempting token refresh');
    return api.post('/refresh-token');
  },
  getProfile: () => {
    console.log('👤 Fetching user profile');
    return api.get('/profile');
  },
  updateProfile: (data: any) => {
    console.log('💾 Updating user profile:', data);
    return api.put('/profile', data);
  },
  // Change password (authenticated)
  changePassword: (currentPassword: string, newPassword: string) => {
    return api.post('/profile/change-password', { current_password: currentPassword, new_password: newPassword });
  },
  // Password reset endpoints
  forgotPassword: (email: string) => {
    console.log('📧 Requesting password reset for:', email);
    return api.post('/forgot-password', { email });
  },
  validateResetToken: (token: string) => {
    console.log('🔑 Validating reset token');
    return api.post('/validate-reset-token', { token });
  },
  resetPassword: (token: string, newPassword: string) => {
    console.log('🔐 Resetting password');
    return api.post('/reset-password', { token, new_password: newPassword });
  },
  logout: () => {
    return api.post('/logout');
  },
};

// MFA (Multi-Factor Authentication) API
export const mfaAPI = {
  // MFA Setup
  initiateSetup: (password: string) => {
    console.log('🔐 Initiating MFA setup');
    return api.post<MFASetupResponse>('/auth/mfa/setup/initiate', { password });
  },
  verifySetup: (setupToken: string, totpCode: string) => {
    console.log('✅ Verifying MFA setup');
    return api.post<MFAVerifyResponse>('/auth/mfa/setup/verify', {
      setup_token: setupToken,
      totp_code: totpCode,
    });
  },

  // MFA Login verification
  verifyLogin: (mfaToken: string, totpCode?: string, recoveryCode?: string, trustDevice?: boolean, deviceName?: string) => {
    console.log('🔐 Verifying MFA login');
    return api.post('/auth/mfa/verify-login', {
      mfa_token: mfaToken,
      totp_code: totpCode,
      recovery_code: recoveryCode,
      trust_device: trustDevice,
      device_name: deviceName,
    });
  },

  // MFA Status and Management
  getStatus: () => {
    console.log('📊 Fetching MFA status');
    return api.get<MFAStatusResponse>('/auth/mfa/status');
  },
  disable: (password: string, totpCode?: string) => {
    console.log('🔒 Disabling MFA');
    return api.post('/auth/mfa/disable', { password, totp_code: totpCode });
  },
  regenerateRecoveryCodes: (password: string) => {
    console.log('🔄 Regenerating recovery codes');
    return api.post('/auth/mfa/recovery-codes/regenerate', { password });
  },

  // Trusted Devices
  getTrustedDevices: () => {
    console.log('📱 Fetching trusted devices');
    return api.get<TrustedDevice[]>('/auth/mfa/trusted-devices');
  },
  revokeTrustedDevice: (deviceId: string) => {
    console.log('❌ Revoking trusted device:', deviceId);
    return api.delete(`/auth/mfa/trusted-devices/${deviceId}`);
  },
  revokeAllTrustedDevices: () => {
    console.log('❌ Revoking all trusted devices');
    return api.post('/auth/mfa/trusted-devices/revoke-all');
  },

  // Admin MFA Settings
  getSystemSettings: () => {
    console.log('⚙️ Fetching system MFA settings');
    return api.get<SystemMFASettings>('/admin/mfa/settings');
  },
  updateSystemSettings: (settings: Partial<SystemMFASettings>) => {
    console.log('⚙️ Updating system MFA settings');
    return api.put<SystemMFASettings>('/admin/mfa/settings', settings);
  },
  getClientSettings: (clientId: number) => {
    console.log('⚙️ Fetching client MFA settings:', clientId);
    return api.get(`/admin/clients/${clientId}/mfa-settings`);
  },
  updateClientSettings: (clientId: number, settings: any) => {
    console.log('⚙️ Updating client MFA settings:', clientId);
    return api.put(`/admin/clients/${clientId}/mfa-settings`, settings);
  },

  // Client primary contact MFA settings (for own client)
  getMyClientSettings: () => {
    console.log('⚙️ Fetching my client MFA settings');
    return api.get('/auth/mfa/client-settings');
  },
  updateMyClientSettings: (settings: { mfa_required?: boolean; device_trust_enabled?: boolean; device_trust_days?: number }) => {
    console.log('⚙️ Updating my client MFA settings');
    return api.put('/auth/mfa/client-settings', settings);
  },
};
